// Notes & Task List layout (#488). The panel's two sections can be REORDERED
// (Task List on top, Notes below, or back) and either one can be EXPANDED to fill
// the panel's visible height, pushing the other below the fold.
//
// Pure: the shape, its validation and the two transitions live here so they are
// unit-testable without a DOM; `hooks/useSplitLayout.js` owns the React state.
//
// Persisted in localStorage under an `omnis:` key, like every other client pref.
// That makes it per USER as well as per browser: sign-out wipes every `omnis:*`
// key (useAuth), so the next account on this browser starts from the default.
// A missing, unparsable or tampered value falls back to the default — a bad pref
// must never hide a section.

export const SPLIT_LAYOUT_KEY = 'omnis:notes-tasks-layout';
// Same-tab sync between mounted instances (the `omnis:*-changed` convention);
// other tabs hear the native `storage` event.
export const SPLIT_LAYOUT_EVENT = 'omnis:notes-tasks-layout-changed';

export const SPLIT_SECTIONS = ['notes', 'tasks'];

export const defaultSplitLayout = () => ({ order: [...SPLIT_SECTIONS], expanded: null });

// Coerce anything into a valid layout: `order` must be a permutation of
// SPLIT_SECTIONS, `expanded` one of them or null. Each field falls back on its own.
export function normalizeSplitLayout(raw) {
    const base = defaultSplitLayout();
    if (!raw || typeof raw !== 'object') return base;
    const order = Array.isArray(raw.order)
        && raw.order.length === SPLIT_SECTIONS.length
        && SPLIT_SECTIONS.every((s) => raw.order.includes(s))
        ? [...raw.order]
        : base.order;
    const expanded = SPLIT_SECTIONS.includes(raw.expanded) ? raw.expanded : null;
    return { order, expanded };
}

export function readSplitLayout() {
    try {
        const raw = localStorage.getItem(SPLIT_LAYOUT_KEY);
        if (raw == null) return defaultSplitLayout();
        return normalizeSplitLayout(JSON.parse(raw));
    } catch (_) {
        return defaultSplitLayout();
    }
}

export function writeSplitLayout(layout) {
    try {
        localStorage.setItem(SPLIT_LAYOUT_KEY, JSON.stringify(normalizeSplitLayout(layout)));
    } catch (_) { /* private mode / quota — the layout simply isn't remembered */ }
}

export const swapOrder = (order) => [...order].reverse();

// Drop `dragged` onto `target`: `dragged` takes the target's slot (with two
// sections, a swap). Unknown names or a drop on itself return `order` unchanged
// (same reference, so callers can skip the write).
export function moveSection(order, dragged, target) {
    const from = order.indexOf(dragged);
    const to = order.indexOf(target);
    if (from < 0 || to < 0 || from === to) return order;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, dragged);
    return next;
}

// Swap while a section is expanded (#544): the two SLOTS trade places — the other
// section becomes the expanded one on top, the previously expanded one goes below
// — and the saved order becomes that painted order, so collapse keeps the swap.
export function swapExpanded(layout) {
    if (!layout.expanded) return { ...layout, order: swapOrder(layout.order) };
    const other = layout.order.find((s) => s !== layout.expanded);
    if (!other) return layout;
    return { order: [other, layout.expanded], expanded: other };
}

export const toggleExpanded = (expanded, section) => (expanded === section ? null : section);

// What is painted, top to bottom. While a section is expanded it goes FIRST (it
// fills the visible height, so the other one sits below the fold — the ticket's
// "global scrolling"); the saved `order` is untouched and returns on collapse.
export function visualOrder({ order, expanded }) {
    if (!expanded) return order;
    return [expanded, ...order.filter((s) => s !== expanded)];
}
